import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SocketContext } from '../../socket';

const HEARTBEAT_MS = 30 * 1000;   // the server's claim lasts 90 s
export const lockKey = (rowId, field) => `${rowId}:${field}`;

/**
 * Who is typing in which cell (docs/MISSED_CALLS_PLAN.md §5.12). The page keeps one map of every held cell,
 * fed by `missedCalls.lock` and by a full list when the socket (re)connects. `claim` opens a cell and keeps
 * the claim alive while it is open; `release` hands it back; `forget` stops refreshing a claim a save already ended.
 *
 * Nothing here ever blocks typing: with no socket, or when the server can't answer, `claim` says yes. The
 * lock is a courtesy between admins, and the server's 409 on save is what actually prevents a silent clobber.
 */
export const useCellLocks = (meId) => {
    const socket = useContext(SocketContext);
    const [locks, setLocks] = useState({});      // { "rowId:field": holder }
    const mine = useRef(new Set());              // cells this page is holding, for the heartbeat and cleanup

    const setLock = useCallback((rowId, field, holder) => {
        setLocks((prev) => {
            const k = lockKey(rowId, field);
            if (!holder) {
                if (!(k in prev)) return prev;
                const next = { ...prev };
                delete next[k];
                return next;
            }
            if (prev[k] && prev[k].admin_id === holder.admin_id && prev[k].at === holder.at) return prev;
            return { ...prev, [k]: holder };
        });
    }, []);

    useEffect(() => {
        if (!socket) return undefined;
        const onLock = (e) => { if (e && e.field) setLock(e.row_id, e.field, e.holder); };
        const loadAll = () => socket.emit('missedCalls.locks', (res) => {
            const next = {};
            for (const l of (res && res.locks) || []) next[lockKey(l.row_id, l.field)] = l.holder;
            setLocks(next);
        });
        socket.on('missedCalls.lock', onLock);
        socket.on('connect', loadAll);
        if (socket.connected) loadAll();
        return () => {
            socket.off('missedCalls.lock', onLock);
            socket.off('connect', loadAll);
        };
    }, [socket, setLock]);

    // Keep this page's own claims alive; the server drops anything older than 90 s.
    useEffect(() => {
        if (!socket) return undefined;
        const beat = setInterval(() => {
            for (const k of mine.current) {
                const [rowId, field] = k.split(':');
                socket.emit('missedCalls.lock', { row_id: Number(rowId), field });
            }
        }, HEARTBEAT_MS);
        return () => clearInterval(beat);
    }, [socket]);

    /** → { ok, holder }. Without a socket the answer is yes: the save is still guarded by the server. */
    const claim = useCallback((rowId, field) => new Promise((resolve) => {
        const k = lockKey(rowId, field);
        if (!socket || !socket.connected) { mine.current.add(k); return resolve({ ok: true, holder: null }); }
        let answered = false;
        const done = (out) => { if (!answered) { answered = true; resolve(out); } };
        socket.emit('missedCalls.lock', { row_id: rowId, field }, (res) => {
            if (res && res.ok) mine.current.add(k);
            done(res || { ok: true, holder: null });
        });
        // A server that never answers must not leave the cell unopenable. Only then: once it has answered, the cell may
        // already be saved or let go, and adding it back would keep claiming it every 30 s.
        setTimeout(() => { if (!answered) mine.current.add(k); done({ ok: true, holder: null }); }, 3000);
    }), [socket]);

    const release = useCallback((rowId, field) => {
        mine.current.delete(lockKey(rowId, field));
        if (socket && socket.connected) socket.emit('missedCalls.unlock', { row_id: rowId, field });
        setLock(rowId, field, null);   // don't wait for the broadcast to come back to us
    }, [socket, setLock]);

    /**
     * A save ended the claim: the server let the cell go as it saved. Only the heartbeat stops — the shared map is left
     * alone, because another admin may already have taken the cell and been announced.
     */
    const forget = useCallback((rowId, field) => {
        mine.current.delete(lockKey(rowId, field));
    }, []);

    // Leaving the page hands back everything this tab was holding.
    const releaseRef = useRef(release);
    releaseRef.current = release;
    useEffect(() => () => {
        for (const k of [...mine.current]) {
            const [rowId, field] = k.split(':');
            releaseRef.current(Number(rowId), field);
        }
    }, []);

    /** Who holds this cell, if it is not us. */
    const heldByOther = useCallback((rowId, field) => {
        const holder = locks[lockKey(rowId, field)];
        return holder && Number(holder.admin_id) !== Number(meId) ? holder : null;
    }, [locks, meId]);

    return useMemo(() => ({ claim, release, forget, heldByOther }), [claim, release, forget, heldByOther]);
};

export default useCellLocks;
